import { useTheme } from '../theme/ThemeContext'
import { chromeToolbarButtonClass } from './ChromeSurface'

function BrokenGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M3 21L21 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function ReloadGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 3v5h5" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M21 21v-5h-5" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export type ImageLoadFailurePlaceholderProps = {
  className?: string
  compact?: boolean
  /** pixel/geek：直角与粗边框，与全局主题一致（与 {@link ChromeToolbarButton} 内部主题一致时应保持相同） */
  angular?: boolean
  /** 探测到的 HTTP 状态码；无则不展示数字 */
  httpStatus?: number | null
  /** 统一的失败诊断原因（来自 diagnoseImageLoadError）；为空则不展示原因行 */
  reason?: string | null
  /** 不带 token 的安全地址片段（host + 截断 path）；为空则不展示地址行 */
  urlLabel?: string | null
  /**
   * 极简模式：只画一个裂图图标，不渲染文案/地址/重载按钮。
   *
   * 用于「宿主自己已经渲染了一套失败 UI」的场景（如参考图选择器底部有「加载失败」标签、
   * 左上角自带重载按钮），避免同一个格子里出现两个刷新按钮、两行「加载失败」。
   */
  minimal?: boolean
  onReload?: () => void
  reloadBusy?: boolean
}

/**
 * 图片加载失败统一占位：短文案 + 可选状态码 + 可选重载（供图库、灯箱、时间线等复用）。
 * 重载按钮固定右上角，避免与 flex 居中层叠在同一容器内导致绝对定位受 align-items 影响。
 */
export function ImageLoadFailurePlaceholder({
  className,
  compact,
  angular,
  httpStatus,
  reason,
  urlLabel,
  minimal,
  onReload,
  reloadBusy,
}: ImageLoadFailurePlaceholderProps) {
  const { theme } = useTheme()
  const angularFromTheme = theme === 'pixel' || theme === 'geek'
  /** 与调用方传入的 pixel/geek 一致时，角标与 Chrome 内层主题对齐 */
  const cornerAngular = angular ?? angularFromTheme

  /** 展示状态码（如果有）：帮助诊断问题，包括 200 但解码失败的情况 */
  const statusCode =
    httpStatus != null && Number.isFinite(httpStatus) ? Math.trunc(httpStatus) : null
  const statusSuffix = statusCode != null ? ` ${statusCode}` : ''
  const line = `加载失败${statusSuffix}`
  const showReason = typeof reason === 'string' && reason.trim().length > 0
  const showUrl = typeof urlLabel === 'string' && urlLabel.trim().length > 0
  const showReload = typeof onReload === 'function'
  const gapCls = compact ? 'gap-[clamp(0.125rem,1.2vmin,0.375rem)]' : 'gap-[clamp(0.25rem,2vmin,0.5rem)]'
  const padCls = compact
    ? 'px-[clamp(0.25rem,2.5%,0.5rem)] py-[clamp(0.375rem,3vmin,0.75rem)]'
    : 'px-[clamp(0.5rem,4%,1rem)] py-[clamp(0.5rem,5vmin,1.25rem)]'
  const glyphCls = compact
    ? 'h-[clamp(1rem,9vmin,1.25rem)] w-[clamp(1rem,9vmin,1.25rem)] shrink-0 opacity-85'
    : 'h-[clamp(1.25rem,12vmin,2rem)] w-[clamp(1.25rem,12vmin,2rem)] shrink-0 opacity-90'
  const reloadGlyphCls = compact
    ? 'h-[clamp(0.875rem,8vmin,1.125rem)] w-[clamp(0.875rem,8vmin,1.125rem)]'
    : 'h-[clamp(1rem,10vmin,1.35rem)] w-[clamp(1rem,10vmin,1.35rem)]'
  const reloadBtnCls = compact
    ? '!m-0 min-h-[1.75rem] min-w-[1.75rem] max-h-[2.25rem] max-w-[2.25rem] !gap-0 !p-1'
    : '!m-0 min-h-[2rem] min-w-[2rem] max-h-[2.5rem] max-w-[2.5rem] !gap-0 !p-1.5'

  // 极简模式：宿主自带失败 UI（底部标签 + 自己的重载按钮），这里只留一个裂图占位
  if (minimal) {
    return (
      <div
        role="img"
        aria-label={showReason ? `${line}：${reason}` : line}
        className={[
          'relative isolate min-h-0 w-full min-w-0 flex-1 overflow-hidden',
          className ?? '',
        ].join(' ')}
      >
        <div className="absolute inset-0 flex items-center justify-center bg-[color-mix(in_srgb,var(--surface-muted)_84%,var(--ink)_5%)] text-ink-soft/45">
          <BrokenGlyph className={glyphCls} />
        </div>
      </div>
    )
  }

  return (
    <div
      role="img"
      aria-label={showReason ? `${line}：${reason}` : line}
      className={['relative isolate min-h-0 w-full min-w-0 flex-1 overflow-hidden', className ?? ''].join(' ')}
    >
      <div
        className={[
          'absolute inset-0 flex flex-col items-center justify-center',
          gapCls,
          padCls,
          'bg-[color-mix(in_srgb,var(--surface-muted)_84%,var(--ink)_5%)] text-ink-soft',
        ].join(' ')}
      >
        <BrokenGlyph className={glyphCls} />
        <span
          className={[
            'max-w-[min(100%,14rem)] text-center font-medium tabular-nums leading-snug text-ink-soft',
            compact ? 'text-[clamp(8px,2.6vmin,10px)]' : 'text-[clamp(10px,3vmin,12px)]',
          ].join(' ')}
        >
          {line}
        </span>
        {showReason ? (
          <span
            className={[
              'block max-w-[min(100%,15rem)] text-center font-normal leading-snug text-ink-soft/90',
              compact
                ? 'text-[clamp(7px,2.1vmin,9px)]'
                : 'text-[clamp(9px,2.4vmin,11px)]',
            ].join(' ')}
          >
            {reason}
          </span>
        ) : null}
        {showUrl ? (
          <span
            className={[
              'block max-w-[min(100%,15rem)] truncate text-center font-mono leading-snug text-ink-soft/70',
              compact
                ? 'text-[clamp(6px,1.8vmin,8px)]'
                : 'text-[clamp(8px,2vmin,10px)]',
            ].join(' ')}
            title={urlLabel}
          >
            {urlLabel}
          </span>
        ) : null}
      </div>
      {showReload ? (
        <div
          role="button"
          tabIndex={reloadBusy ? -1 : 0}
          aria-label="重新加载"
          aria-disabled={reloadBusy || undefined}
          onClick={(e) => {
            e.stopPropagation()
            if (!reloadBusy) onReload()
          }}
          onKeyDown={(e) => {
            if (reloadBusy) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onReload()
            }
          }}
          className={[
            chromeToolbarButtonClass(cornerAngular),
            '!absolute z-[8]',
            'end-[clamp(0.25rem,3.5%,0.625rem)] top-[clamp(0.25rem,3.5%,0.625rem)]',
            '!border-line/45 !bg-[color-mix(in_srgb,var(--panel)_40%,transparent)] shadow-sm backdrop-blur-[2px]',
            'hover:!border-line/60 hover:!bg-[color-mix(in_srgb,var(--panel)_58%,transparent)]',
            'opacity-[0.82] transition-[opacity,background-color,border-color] hover:opacity-100 aria-disabled:opacity-40 motion-reduce:transition-none',
            cornerAngular ? '!rounded-none !border-2' : '!rounded-md',
            reloadBtnCls,
          ].join(' ')}
        >
          <ReloadGlyph className={[reloadGlyphCls, 'opacity-95'].join(' ')} />
        </div>
      ) : null}
    </div>
  )
}
